import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  HStack,
  Switch,
  Textarea,
  useToast,
  Heading,
  Text,
  Badge,
  Tooltip,
  useColorModeValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  FiKey,
  FiLock,
  FiShoppingBag,
  FiFileText,
  FiDollarSign,
  FiSettings,
  FiCopy,
  FiCheck,
  FiEye,
  FiEyeOff,
  FiAlertTriangle,
} from "react-icons/fi";
import { DeliveryCompany } from "../../../../entity/DeliveryCompany";
import { useTranslation } from "react-i18next";
import { StoreDeliveryConfigurations } from "../../../../entity/StoreDeliveryConfiguration";
import { WSDeliveryInstance } from "../../../../services/remote/delivery/WSDelivery";
import { selectStore } from "../../../../store/selectors/StoreSelector";
import { useSelector } from "react-redux";
import { selectStoreConfig } from "../../../../store/selectors/DeliverySelector";
import { COLORS } from "../../../../utils/Colors";

interface ConnectDeliveryCompanyProps {
  isOpen: boolean;
  onClose: () => void;
  company: DeliveryCompany | null;
  onSuccess?: () => void;
}

type FieldErrors = Partial<Record<keyof StoreDeliveryConfigurations, string>>;

const BRAND = COLORS.Brand.Primary;

const emptyConfig = (
  companyId: string,
  storeId: string,
  storeName: string,
): StoreDeliveryConfigurations => ({
  storeId,
  companyId,
  apiKey: "",
  apiSecret: "",
  storeName,
  deliveryCost: 0,
  returnCost: 0,
  pickupRegion: "",
  pickupCity: "",
  taxRegistration: "",
  specialNotes: "",
  fragile: false,
  allowPackageOpening: false,
});

// Section header used for every group of fields, keeps icon + title + optional
// badge consistent across the drawer instead of repeating markup.
const SectionHeader = ({
  icon,
  title,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  badge?: string;
}) => (
  <HStack spacing={2} mb={3}>
    <Box color={BRAND}>{icon}</Box>
    <Heading size="sm" fontWeight="600">
      {title}
    </Heading>
    {badge && (
      <Badge colorScheme="orange" fontSize="10px" borderRadius="full" px={2}>
        {badge}
      </Badge>
    )}
  </HStack>
);

export const ConnectDeliveryCompany = ({
  isOpen,
  onClose,
  company,
  onSuccess,
}: ConnectDeliveryCompanyProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const store = useSelector(selectStore);
  const storeConfig = useSelector(selectStoreConfig);

  const [selectedStoreConfig, setSelectedStoreConfig] =
    useState<StoreDeliveryConfigurations | null>(null);

  // Color mode values — kept in one place so the drawer follows the app's
  // existing light/dark tokens rather than hardcoding grays.
  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const readOnlyBg = useColorModeValue("gray.100", "gray.900");
  const mutedText = useColorModeValue("gray.500", "gray.400");

  const [config, setConfig] = useState<StoreDeliveryConfigurations>(() =>
    emptyConfig(company?.id ?? "", store?.id ?? "", store?.name ?? ""),
  );
  const [initialConfig, setInitialConfig] = useState(config);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const isEditMode = !!config.id;

  const isDirty = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(initialConfig),
    [config, initialConfig],
  );

  useEffect(() => {
    if (!company || !store) return;

    const existing =
      storeConfig?.find((c) => c?.companyId === company.id) || null;
    setSelectedStoreConfig(existing);

    const next = existing
      ? { ...existing, storeId: store.id, companyId: company.id! }
      : emptyConfig(company.id!, store.id, store.name || "");

    setConfig(next);
    setInitialConfig(next);
    setErrors({});
  }, [storeConfig, store, company]);

  const validate = useCallback(
    (values: StoreDeliveryConfigurations): FieldErrors => {
      const next: FieldErrors = {};

      if (!values.apiKey?.trim()) {
        next.apiKey = t("deliveryConfig.apiKeyRequired");
      }
      if (company?.apiSecretIsRequired && !values.apiSecret?.trim()) {
        next.apiSecret = t("deliveryConfig.secretKeyRequired");
      }
      if (!values.storeName?.trim()) {
        next.storeName = t("deliveryConfig.storeNameRequired");
      }
      if (values.deliveryCost != null && values.deliveryCost < 0) {
        next.deliveryCost = t("deliveryConfig.costMustBePositive");
      }
      if (values.returnCost != null && values.returnCost < 0) {
        next.returnCost = t("deliveryConfig.costMustBePositive");
      }

      return next;
    },
    [company, t],
  );

  const handleConnect = async () => {
    if (!company) return;

    const validationErrors = validate(config);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast({
        title: t("deliveryConfig.validationError"),
        description: t("deliveryConfig.checkHighlightedFields"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        await WSDeliveryInstance.updateDeliveryCompanyConfigurations(config);
      } else {
        await WSDeliveryInstance.connectDeliveryCompany(config);
      }

      toast({
        title: isEditMode
          ? t("deliveryConfig.updateSuccess")
          : t("deliveryConfig.connectionSuccess"),
        description: t(
          isEditMode
            ? "deliveryConfig.updatedSuccessfully"
            : "deliveryConfig.connectedSuccessfully",
          { companyName: company.name },
        ),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess?.();
      setInitialConfig(config);
      onClose();
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "connecting to"} delivery company:`,
        error,
      );
      toast({
        title: isEditMode
          ? t("deliveryConfig.updateError")
          : t("deliveryConfig.connectionError"),
        description: t(
          isEditMode
            ? "deliveryConfig.failedToUpdate"
            : "deliveryConfig.failedToConnect",
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: keyof StoreDeliveryConfigurations,
    value: any,
  ) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    // Clear the field's error as soon as the person starts fixing it,
    // rather than making them wait for the next submit attempt.
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAttemptClose = () => {
    if (isDirty && !loading) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleDiscardAndClose = () => {
    setConfirmCloseOpen(false);
    setConfig(initialConfig);
    setErrors({});
    onClose();
  };

  const handleCopyBaseUrl = async () => {
    if (!company?.baseUrl) return;
    try {
      await navigator.clipboard.writeText(company.baseUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can fail silently (permissions, insecure context);
      // it's a convenience action, not worth surfacing an error toast for.
    }
  };

  if (!company) return null;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleAttemptClose}
        size={{ base: "full", md: "xl" }}
      >
        <DrawerOverlay />
        <DrawerContent bg={cardBg}>
          <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
            <HStack justify="space-between" align="flex-start">
              <Box>
                <Heading size="md">
                  {t(
                    isEditMode
                      ? "deliveryConfig.updateCompanyConfig"
                      : "deliveryConfig.connectToCompany",
                    { companyName: company.name },
                  )}
                </Heading>
                <Text fontSize="sm" color={mutedText} mt={1}>
                  {isEditMode
                    ? t("deliveryConfig.editSubtitle", {
                        companyName: company.name,
                      })
                    : t("deliveryConfig.connectSubtitle", {
                        companyName: company.name,
                      })}
                </Text>
              </Box>
              <DrawerCloseButton position="static" />
            </HStack>
          </DrawerHeader>

          <DrawerBody py={5}>
            <VStack spacing={5} align="stretch">
              {/* API Configuration */}
              <Box
                bg={sectionBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                p={4}
              >
                <SectionHeader
                  icon={<FiKey size={18} />}
                  title={t("deliveryConfig.apiConfiguration")}
                />

                <FormControl isRequired isInvalid={!!errors.apiKey} mb={4}>
                  <FormLabel fontSize="sm">
                    {company.apiKeyLabel || t("deliveryConfig.accessKey")}
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FiKey size={16} color="gray" />
                    </InputLeftElement>
                    <Input
                      value={config.apiKey}
                      onChange={(e) =>
                        handleInputChange("apiKey", e.target.value)
                      }
                      placeholder={
                        company.apiKeyLabel ||
                        t("deliveryConfig.enterAccessKey")
                      }
                      bg={cardBg}
                      borderColor={borderColor}
                      _focusVisible={{
                        borderColor: BRAND,
                        boxShadow: `0 0 0 1px ${BRAND}`,
                      }}
                    />
                  </InputGroup>
                  {errors.apiKey && (
                    <FormErrorMessage>{errors.apiKey}</FormErrorMessage>
                  )}
                </FormControl>

                {company.apiSecretIsRequired && (
                  <FormControl isRequired isInvalid={!!errors.apiSecret} mb={4}>
                    <FormLabel fontSize="sm">
                      {company.apiSecretLabel || t("deliveryConfig.secretKey")}
                    </FormLabel>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <FiLock size={16} color="gray" />
                      </InputLeftElement>
                      <Input
                        type={showSecret ? "text" : "password"}
                        value={config.apiSecret || ""}
                        onChange={(e) =>
                          handleInputChange("apiSecret", e.target.value)
                        }
                        placeholder={
                          company.apiSecretLabel ||
                          t("deliveryConfig.enterSecretKey")
                        }
                        bg={cardBg}
                        borderColor={borderColor}
                        _focusVisible={{
                          borderColor: BRAND,
                          boxShadow: `0 0 0 1px ${BRAND}`,
                        }}
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={
                            showSecret
                              ? t("deliveryConfig.hideSecret")
                              : t("deliveryConfig.showSecret")
                          }
                          icon={
                            showSecret ? (
                              <FiEyeOff size={16} />
                            ) : (
                              <FiEye size={16} />
                            )
                          }
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowSecret((v) => !v)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    {errors.apiSecret ? (
                      <FormErrorMessage>{errors.apiSecret}</FormErrorMessage>
                    ) : (
                      <FormHelperText fontSize="xs">
                        {t("deliveryConfig.secretKeptEncrypted")}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel fontSize="sm">
                    {t("deliveryConfig.baseUrl")}
                  </FormLabel>
                  <InputGroup>
                    <Input
                      value={company.baseUrl}
                      isReadOnly
                      bg={readOnlyBg}
                      borderColor={borderColor}
                      fontFamily="mono"
                      fontSize="sm"
                    />
                    <InputRightElement>
                      <Tooltip
                        label={
                          copied
                            ? t("deliveryConfig.copied")
                            : t("deliveryConfig.copyToClipboard")
                        }
                        closeOnClick={false}
                      >
                        <IconButton
                          aria-label={t("deliveryConfig.copyToClipboard")}
                          icon={
                            copied ? (
                              <FiCheck size={16} color="green" />
                            ) : (
                              <FiCopy size={16} />
                            )
                          }
                          size="sm"
                          variant="ghost"
                          onClick={handleCopyBaseUrl}
                        />
                      </Tooltip>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
              </Box>

              {/* Store Information */}
              <Box
                bg={sectionBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                p={4}
              >
                <SectionHeader
                  icon={<FiShoppingBag size={18} />}
                  title={t("deliveryConfig.storeInformation")}
                />

                <FormControl isRequired isInvalid={!!errors.storeName} mb={4}>
                  <FormLabel fontSize="sm">
                    {t("deliveryConfig.storeName")}
                  </FormLabel>
                  <Input
                    value={config.storeName}
                    onChange={(e) =>
                      handleInputChange("storeName", e.target.value)
                    }
                    placeholder={t("deliveryConfig.enterStoreName")}
                    bg={cardBg}
                    borderColor={borderColor}
                    _focusVisible={{
                      borderColor: BRAND,
                      boxShadow: `0 0 0 1px ${BRAND}`,
                    }}
                  />
                  {errors.storeName && (
                    <FormErrorMessage>{errors.storeName}</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">
                    {t("deliveryConfig.taxRegistration")}
                  </FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FiFileText size={16} color="gray" />
                    </InputLeftElement>
                    <Input
                      value={config.taxRegistration}
                      onChange={(e) =>
                        handleInputChange("taxRegistration", e.target.value)
                      }
                      placeholder={t("deliveryConfig.enterTaxRegistration")}
                      bg={cardBg}
                      borderColor={borderColor}
                      _focusVisible={{
                        borderColor: BRAND,
                        boxShadow: `0 0 0 1px ${BRAND}`,
                      }}
                    />
                  </InputGroup>
                </FormControl>
              </Box>

              {/* Pricing Configuration */}
              <Box
                bg={sectionBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                p={4}
              >
                <SectionHeader
                  icon={<FiDollarSign size={18} />}
                  title={t("deliveryConfig.pricingConfiguration")}
                />

                <HStack spacing={4} align="flex-start">
                  <FormControl flex={1} isInvalid={!!errors.deliveryCost}>
                    <FormLabel fontSize="sm">
                      {t("deliveryConfig.deliveryCost")}
                    </FormLabel>
                    <NumberInput
                      value={config.deliveryCost || 0}
                      onChange={(_, value) =>
                        handleInputChange(
                          "deliveryCost",
                          Number.isNaN(value) ? 0 : value,
                        )
                      }
                      min={0}
                      precision={2}
                      bg={cardBg}
                    >
                      <NumberInputField
                        borderColor={borderColor}
                        _focusVisible={{
                          borderColor: BRAND,
                          boxShadow: `0 0 0 1px ${BRAND}`,
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    {errors.deliveryCost && (
                      <FormErrorMessage>{errors.deliveryCost}</FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl flex={1} isInvalid={!!errors.returnCost}>
                    <FormLabel fontSize="sm">
                      {t("deliveryConfig.returnCost")}
                    </FormLabel>
                    <NumberInput
                      value={config.returnCost || 0}
                      onChange={(_, value) =>
                        handleInputChange(
                          "returnCost",
                          Number.isNaN(value) ? 0 : value,
                        )
                      }
                      min={0}
                      precision={2}
                      bg={cardBg}
                    >
                      <NumberInputField
                        borderColor={borderColor}
                        _focusVisible={{
                          borderColor: BRAND,
                          boxShadow: `0 0 0 1px ${BRAND}`,
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    {errors.returnCost && (
                      <FormErrorMessage>{errors.returnCost}</FormErrorMessage>
                    )}
                  </FormControl>
                </HStack>
              </Box>

              {/* Service Options */}
              <Box
                bg={sectionBg}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                p={4}
              >
                <SectionHeader
                  icon={<FiSettings size={18} />}
                  title={t("deliveryConfig.serviceOptions")}
                />

                <FormControl mb={4}>
                  <HStack justify="space-between" mb={1}>
                    <FormLabel fontSize="sm" mb={0}>
                      {t("deliveryConfig.specialNotes")}
                    </FormLabel>
                    <Text fontSize="xs" color={mutedText}>
                      {(config.specialNotes || "").length}/300
                    </Text>
                  </HStack>
                  <Textarea
                    value={config.specialNotes}
                    onChange={(e) =>
                      handleInputChange(
                        "specialNotes",
                        e.target.value.slice(0, 300),
                      )
                    }
                    placeholder={t("deliveryConfig.enterSpecialNotes")}
                    rows={3}
                    bg={cardBg}
                    borderColor={borderColor}
                    _focusVisible={{
                      borderColor: BRAND,
                      boxShadow: `0 0 0 1px ${BRAND}`,
                    }}
                  />
                </FormControl>

                <VStack
                  spacing={0}
                  align="stretch"
                  borderTopWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack
                    justify="space-between"
                    py={3}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="500">
                        {t("deliveryConfig.fragile")}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>
                        {t("deliveryConfig.fragileHelp")}
                      </Text>
                    </Box>
                    <Switch
                      colorScheme="orange"
                      isChecked={config.fragile}
                      onChange={(e) =>
                        handleInputChange("fragile", e.target.checked)
                      }
                    />
                  </HStack>

                  <HStack justify="space-between" py={3}>
                    <Box>
                      <Text fontSize="sm" fontWeight="500">
                        {t("deliveryConfig.allowPackageOpening")}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>
                        {t("deliveryConfig.allowPackageOpeningHelp")}
                      </Text>
                    </Box>
                    <Switch
                      colorScheme="orange"
                      isChecked={config.allowPackageOpening}
                      onChange={(e) =>
                        handleInputChange(
                          "allowPackageOpening",
                          e.target.checked,
                        )
                      }
                    />
                  </HStack>
                </VStack>
              </Box>
            </VStack>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" borderColor={borderColor}>
            <HStack spacing={3} justify="space-between" w="full">
              <Text fontSize="xs" color={mutedText}>
                {isDirty ? t("deliveryConfig.unsavedChanges") : ""}
              </Text>
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  onClick={handleAttemptClose}
                  isDisabled={loading}
                >
                  {t("deliveryConfig.cancel")}
                </Button>
                <Button
                  bg={BRAND}
                  color="white"
                  _hover={{ bg: "#d4551f" }}
                  _active={{ bg: "#c04c1b" }}
                  onClick={handleConnect}
                  isLoading={loading}
                  loadingText={t(
                    isEditMode
                      ? "deliveryConfig.updating"
                      : "deliveryConfig.connecting",
                  )}
                >
                  {t(
                    isEditMode
                      ? "deliveryConfig.update"
                      : "deliveryConfig.connect",
                  )}
                </Button>
              </HStack>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Confirms discarding unsaved changes instead of losing them silently */}
      <AlertDialog
        isOpen={confirmCloseOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmCloseOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md" fontWeight="600">
              <HStack>
                <FiAlertTriangle size={18} color={BRAND} />
                <Text>{t("deliveryConfig.discardChangesTitle")}</Text>
              </HStack>
            </AlertDialogHeader>
            <AlertDialogBody>
              {t("deliveryConfig.discardChangesBody")}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="outline"
                onClick={() => setConfirmCloseOpen(false)}
              >
                {t("deliveryConfig.keepEditing")}
              </Button>
              <Button colorScheme="red" onClick={handleDiscardAndClose} ml={3}>
                {t("deliveryConfig.discard")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
